import React, { useEffect, useState } from "react";
import { dashboardApi, SimplyloanStatsResponse } from "../services/dashboardApi";
import { Users, TrendingUp, Calendar, AlertCircle, Clock } from "lucide-react";
import MainLayout from "../components/layout/MainLayout";

export default function SimplyloanPage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<SimplyloanStatsResponse | null>(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dashboardApi.getSimplyloanStats();
            setStats(data);
        } catch (err: any) {
            console.error("Failed to fetch simplyloan stats:", err);
            setError(err.message || "Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center p-12 h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <span className="ml-3 text-indigo-600 font-medium tracking-wide">Connecting to database...</span>
                </div>
            </MainLayout>
        );
    }

    if (error) {
        return (
            <MainLayout>
                <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                    <div>
                        <h3 className="font-semibold text-red-800">Connection Error</h3>
                        <p className="text-sm mt-1">{error}</p>
                        <button
                            onClick={fetchStats}
                            className="mt-3 bg-red-100 text-red-700 px-4 py-2 rounded font-medium hover:bg-red-200 transition-colors text-sm"
                        >
                            Retry Connection
                        </button>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (!stats) return null;

    return (
        <MainLayout>
            <div className="space-y-6">
                {/* Header section with gradient background */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-8 shadow-md text-white overflow-hidden relative">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-24 h-24 bg-blue-400 opacity-20 rounded-full blur-xl"></div>

                    <div className="relative z-10">
                        <h2 className="text-3xl font-bold tracking-tight">Simplyloan Subscribers</h2>
                        <p className="text-blue-100 mt-2 max-w-2xl text-lg">
                            Monitor real-time lead generation from the simplyloan_leads MySQL database.
                        </p>
                        {stats.timestamp_column_used ? (
                            <p className="text-blue-200 mt-1 text-sm flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-full bg-green-400 mr-1"></span>
                                Tracking daily metrics using `{stats.timestamp_column_used}` column
                            </p>
                        ) : (
                            <p className="text-yellow-300 mt-1 text-sm flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 mr-1 animate-pulse"></span>
                                Warning: No timestamp column detected in the database. Only total count is available.
                            </p>
                        )}
                    </div>
                </div>

                {/* KPI Cards section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Total Subscribers Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 text-gray-500 mb-2 font-medium uppercase tracking-wider text-xs">
                            <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                                <Users className="w-5 h-5" />
                            </div>
                            Total Subscribers
                        </div>
                        <div className="text-4xl font-bold text-gray-900 mt-2">
                            {stats.total.toLocaleString()}
                        </div>
                        <div className="mt-4 border-t border-gray-50 pt-4 text-sm text-gray-500 flex justify-between items-center">
                            <span>Total records in simplyloan_leads</span>
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">All Time</span>
                        </div>
                    </div>

                    {/* Today's New Subscribers Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col hover:shadow-md transition-shadow relative overflow-hidden">
                        {stats.today > 0 && (
                            <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 opacity-10 rounded-bl-full" />
                        )}
                        <div className="flex items-center gap-3 text-gray-500 mb-2 font-medium uppercase tracking-wider text-xs">
                            <div className="p-2 bg-green-50 rounded-lg text-emerald-600">
                                <TrendingUp className="w-5 h-5" />
                            </div>
                            New Today
                        </div>
                        <div className="text-4xl font-bold text-gray-900 mt-2 flex items-baseline gap-2">
                            {stats.today.toLocaleString()}
                            {stats.today > 0 && (
                                <span className="text-sm font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                    Active
                                </span>
                            )}
                        </div>
                        <div className="mt-4 border-t border-gray-50 pt-4 text-sm text-gray-500 flex justify-between items-center">
                            <span>Leads generated today</span>
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> Today
                            </span>
                        </div>
                    </div>
                </div>

                {/* Recent Subscribers Table */}
                {stats.recent_subscribers && stats.recent_subscribers.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                            <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                                <Clock className="w-4 h-4 text-indigo-600" />
                                Recent Subscribers
                            </h3>
                            <span className="text-xs font-medium bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full shadow-sm">
                                Last {stats.recent_subscribers.length}
                            </span>
                        </div>
                        <div className="overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-white sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Timestamp</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">First Name</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">UTM Source</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Redirect URL</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {stats.recent_subscribers.map((row, idx) => {
                                        // Attempt to find timestamp by the used column or fallback aliases
                                        const tsColInfo = stats.timestamp_column_used || 'time_stamp';
                                        const timestampVal = row[tsColInfo] || row['created_at'] || row['date'] || '-';

                                        // Format timestamp slightly if it starts with a year, keeping it compact
                                        let displayTime = String(timestampVal);
                                        if (displayTime.includes('T')) {
                                            displayTime = displayTime.split('T').join(' ').substring(0, 16);
                                        } else if (displayTime.length > 16) {
                                            displayTime = displayTime.substring(0, 16);
                                        }

                                        return (
                                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-2 whitespace-nowrap text-gray-500 text-xs font-medium">
                                                    {displayTime}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-gray-900 border-l border-gray-50 text-sm">
                                                    {row['email'] || '-'}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-gray-700 border-l border-gray-50 text-xs">
                                                    {row['firstname'] || row['first_name'] || row['name'] || '-'}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-gray-600 border-l border-gray-50 text-xs">
                                                    {row['phone'] || '-'}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-gray-500 border-l border-gray-50">
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 uppercase tracking-wide">
                                                        {row['utm_source'] || 'direct'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-gray-500 border-l border-gray-50 text-[11px] max-w-[150px] truncate" title={row['redirect_url'] || ''}>
                                                    {row['redirect_url'] || '-'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Daily Breakdown Table */}
                {stats.daily_counts && stats.daily_counts.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-800">Recent Growth</h3>
                            <span className="text-xs font-medium bg-white border border-gray-200 text-gray-600 px-3 py-1 rounded-full shadow-sm">
                                Last {stats.daily_counts.length} Days
                            </span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-white">
                                    <tr>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            New Subscribers
                                        </th>
                                        <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Activity level
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {stats.daily_counts.map((day, idx) => {
                                        // Calculate simple relative bar width based on max in recent days
                                        const maxCount = Math.max(...stats.daily_counts.map(d => d.count), 1);
                                        const barWidth = `${Math.max((day.count / maxCount) * 100, 2)}%`;

                                        return (
                                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {day.date}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold">
                                                    +{day.count.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-1/2">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-full bg-gray-100 rounded-full h-2.5 max-w-xs">
                                                            <div
                                                                className="bg-indigo-500 h-2.5 rounded-full"
                                                                style={{ width: barWidth }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
}
